import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Trophy,
  House as HouseIcon,
  MegaphoneSimple,
  Coin,
  Briefcase,
  SealCheck,
  Clock,
  Users,
  MapPin,
  Globe,
  ArrowUpRight,
} from "@phosphor-icons/react";
import Layout from "@/components/Layout";
import { calls } from "@/data/mockData";

const typeIcon = {
  RESIDENCIA: HouseIcon,
  BECA: Coin,
  PREMIO: Trophy,
  "OPEN CALL": MegaphoneSimple,
  COMISIÓN: Briefcase,
};

const typeColor = {
  RESIDENCIA: "#00FF66",
  BECA: "#FF3E00",
  PREMIO: "#FFD600",
  "OPEN CALL": "#0033FF",
  COMISIÓN: "#FF0033",
};

const filters = ["TODAS", "RESIDENCIA", "BECA", "PREMIO", "OPEN CALL", "COMISIÓN"];

function deadlineTone(days) {
  if (days <= 3) return { color: "#FF0033", label: "URGENTE" };
  if (days <= 14) return { color: "#FF3E00", label: "PRONTO" };
  if (days <= 30) return { color: "#FFD600", label: "ESTE MES" };
  return { color: "#00FF66", label: "ABIERTA" };
}

export default function Calls() {
  const [filter, setFilter] = useState("TODAS");
  const [onlyVerified, setOnlyVerified] = useState(false);

  const list = useMemo(() => {
    return calls.filter((c) => {
      const byType = filter === "TODAS" || c.type === filter;
      const byVerified = !onlyVerified || c.verified;
      return byType && byVerified;
    });
  }, [filter, onlyVerified]);

  const featured = calls.find((c) => c.featured);
  const totalPrize = calls.reduce((acc, c) => acc + (c.prizeValue || 0), 0);
  const urgentCount = calls.filter((c) => c.daysLeft <= 7).length;

  return (
    <Layout wide>
      <div data-testid="calls-page">
        <header className="p-4 md:p-8 border-b border-[#27272A]">
          <div className="flex items-center gap-3 mb-2 font-mono text-[10px] tracking-[0.3em]">
            <span className="h-1.5 w-1.5 bg-[#FFD600] live-dot" />
            <span className="text-[#FFD600]">OPORTUNIDADES · {calls.length} ABIERTAS</span>
            <span className="text-[#525252]">· {urgentCount} CIERRAN ESTA SEMANA</span>
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter uppercase leading-none">
            Becas &
            <br />
            <span className="text-[#FF3E00]">convocatorias</span>.
          </h1>
          <p className="font-mono text-sm text-[#A1A1AA] mt-5 max-w-2xl leading-relaxed">
            // TABLERO EDITORIAL CURADO. RESIDENCIAS, BECAS, PREMIOS Y OPEN CALLS
            VERIFICADOS. FILTRA, POSTULA, GANA.
          </p>

          <div className="mt-8 grid grid-cols-3 max-w-xl border border-[#27272A]">
            <StatCol n={calls.length} label="CONVOCATORIAS" />
            <StatCol n={`€${(totalPrize / 1000).toFixed(0)}K`} label="EN PREMIOS" divider />
            <StatCol n={urgentCount} label="URGENTES" divider color="#FF0033" />
          </div>
        </header>

        {/* Featured */}
        {featured ? (
          <Link
            to={`/convocatorias/${featured.id}`}
            data-testid="featured-call"
            className="relative block overflow-hidden border-b border-[#27272A] group"
          >
            <div className="grid grid-cols-1 md:grid-cols-[1fr_600px] min-h-[380px]">
              <div className="relative overflow-hidden order-last md:order-first">
                <img
                  src={featured.cover}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/40 to-transparent" />
              </div>
              <div className="relative p-5 md:p-8 flex flex-col justify-between bg-[#050505]">
                <div>
                  <div className="flex items-center gap-2 flex-wrap mb-4">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] bg-[#FFD600] text-black px-2 py-1">
                      ⭐ DESTACADA
                    </span>
                    <span
                      className="font-mono text-[10px] uppercase tracking-[0.22em] px-2 py-1 border"
                      style={{
                        color: typeColor[featured.type],
                        borderColor: typeColor[featured.type],
                      }}
                    >
                      {featured.type}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA]">
                      {featured.discipline}
                    </span>
                  </div>
                  <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider mb-2">
                    // {featured.org.toUpperCase()}
                  </div>
                  <h2 className="font-display text-3xl md:text-5xl font-black tracking-tighter uppercase leading-[1.05]">
                    {featured.title}
                  </h2>
                  <p className="text-[15px] leading-[1.6] text-[#E5E5E5] mt-4 max-w-xl">
                    {featured.summary}
                  </p>
                </div>

                <div className="mt-6 pt-6 border-t border-[#27272A] grid grid-cols-3 gap-4 items-end">
                  <div>
                    <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em]">
                      PREMIO
                    </div>
                    <div className="font-display font-black text-lg text-[#FF3E00]">
                      {featured.prize}
                    </div>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em]">
                      CIERRA EN
                    </div>
                    <div className="font-display font-black text-lg">
                      {featured.daysLeft} días
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-2 font-mono uppercase text-[10px] tracking-[0.22em] bg-[#FF3E00] text-white px-4 py-3 justify-self-end w-fit btn-brutal">
                    Postular <ArrowUpRight size={12} weight="bold" />
                  </span>
                </div>
              </div>
            </div>
          </Link>
        ) : null}

        {/* Filters */}
        <div className="sticky top-0 z-10 bg-[#050505]/90 backdrop-blur-lg border-b border-[#27272A] p-4 md:p-5 flex items-center gap-3 flex-wrap">
          {filters.map((f) => (
            <button
              key={f}
              data-testid={`call-filter-${f.toLowerCase()}`}
              onClick={() => setFilter(f)}
              className={`font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2 border transition-colors ${
                filter === f
                  ? "bg-[#FF3E00] text-white border-[#FF3E00]"
                  : "border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#F4F4F5]"
              }`}
            >
              {f}
            </button>
          ))}
          <label className="ml-auto flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA] cursor-pointer">
            <input
              type="checkbox"
              checked={onlyVerified}
              onChange={(e) => setOnlyVerified(e.target.checked)}
              className="accent-[#FF3E00]"
            />
            Sólo verificadas
          </label>
        </div>

        {/* List */}
        <section className="p-4 md:p-6 grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
          {list.map((c) => {
            const Icon = typeIcon[c.type] || Trophy;
            const tone = deadlineTone(c.daysLeft);
            return (
              <Link
                key={c.id}
                to={`/convocatorias/${c.id}`}
                data-testid={`call-card-${c.id}`}
                className="group relative block border border-[#27272A] hover:border-[#FF3E00] bg-[#0a0a0a] overflow-hidden transition-colors"
              >
                <div className="grid grid-cols-[140px_1fr] md:grid-cols-[180px_1fr]">
                  {/* Cover thumb */}
                  <div className="relative overflow-hidden">
                    <img
                      src={c.cover}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0a0a0a]/60" />
                    <div
                      className="absolute top-2 left-2 h-8 w-8 inline-flex items-center justify-center"
                      style={{
                        background: typeColor[c.type],
                        color: "#050505",
                      }}
                    >
                      <Icon size={14} weight="fill" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4 md:p-5 flex flex-col gap-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="font-mono text-[9px] uppercase tracking-[0.22em] border px-1.5 py-0.5"
                        style={{
                          color: typeColor[c.type],
                          borderColor: typeColor[c.type],
                        }}
                      >
                        {c.type}
                      </span>
                      <span
                        className="font-mono text-[9px] uppercase tracking-[0.22em] px-1.5 py-0.5"
                        style={{ background: tone.color, color: "#050505" }}
                      >
                        {tone.label} · {c.daysLeft}D
                      </span>
                      {c.verified ? (
                        <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#00FF66] inline-flex items-center gap-1">
                          <SealCheck size={10} weight="fill" /> Verif.
                        </span>
                      ) : c.community ? (
                        <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#FF3E00]">
                          ✦ COMUNIDAD
                        </span>
                      ) : null}
                    </div>

                    <div>
                      <h3 className="font-display font-black text-lg md:text-xl tracking-tight leading-tight line-clamp-2">
                        {c.title}
                      </h3>
                      <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider mt-1">
                        // {c.org.toUpperCase()}
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-2 pt-2 mt-auto border-t border-[#27272A]">
                      <div className="min-w-0">
                        <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em]">
                          PREMIO
                        </div>
                        <div className="font-display font-black text-sm md:text-base text-[#FF3E00] truncate">
                          {c.prize}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 font-mono text-[10px] text-[#A1A1AA] tracking-wider whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">
                          {c.remote ? <Globe size={10} /> : <MapPin size={10} />}
                          {c.location}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Users size={10} weight="fill" />
                          {c.applicants} postulantes
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </section>

        {/* Propose */}
        <section className="px-4 md:px-6 pb-10">
          <div className="border border-dashed border-[#27272A] p-6 md:p-8 bg-[#0a0a0a] flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <div className="font-mono text-[10px] text-[#FFD600] tracking-[0.3em] mb-2">
                ¿ERES GALERÍA, MUSEO O COLECTIVO?
              </div>
              <h3 className="font-display font-black text-2xl md:text-3xl uppercase tracking-tighter">
                Publica una convocatoria
              </h3>
              <p className="font-mono text-[11px] text-[#A1A1AA] tracking-wider mt-2 max-w-lg">
                LLEGA DIRECTO A +1,900 ARTISTAS ACTIVOS. VERIFICACIÓN EDITORIAL EN 24H.
                TARIFA SEGÚN TAMAÑO DEL PREMIO.
              </p>
            </div>
            <button
              data-testid="publish-call-btn"
              className="btn-brutal font-mono uppercase text-xs tracking-[0.22em] bg-[#FFD600] text-black px-6 py-3 font-bold"
            >
              + Publicar convocatoria
            </button>
          </div>
        </section>
      </div>
    </Layout>
  );
}

function StatCol({ n, label, divider, color }) {
  return (
    <div
      className={`p-4 text-center ${
        divider ? "border-l border-[#27272A]" : ""
      }`}
    >
      <div
        className="font-display font-black text-2xl md:text-3xl tracking-tight"
        style={color ? { color } : {}}
      >
        {n}
      </div>
      <div className="font-mono text-[9px] text-[#A1A1AA] tracking-[0.22em] mt-1">
        {label}
      </div>
    </div>
  );
}
